let subscriberCounter = 0;

/**
 * One symbol and resolution on a datafeed with the TradingView contract
 * (datafeed.js, E2): onReady → resolveSymbol, then getBars and
 * subscribeBars / unsubscribeBars. After `close()` nothing reaches the
 * caller any more: late history answers and bars of the old stream are dropped.
 *
 * @param {object} datafeed - instance from createDatafeed()
 * @param {object} params
 * @param {string} params.symbol
 * @param {string} params.resolution - TV resolution, e.g. "1", "60", "D", "15S"
 * @param {(symbolInfo: object) => void} params.onResolved - getBars and subscribe work from here on
 */
export const openDatafeedSession = (datafeed, { symbol, resolution, onResolved }) => {
  let isClosed = false;
  let symbolInfo = null;
  let subscriberUid = null;

  datafeed.onReady(() => {
    if (isClosed) return;

    datafeed.resolveSymbol(symbol, (resolvedSymbolInfo) => {
      if (isClosed) return;

      symbolInfo = resolvedSymbolInfo;
      onResolved(resolvedSymbolInfo);
    });
  });

  return {
    /**
     * @param {{ from: number, to: number, countBack: number, firstDataRequest: boolean }} periodParams - seconds
     * @param {(bars: object[], noData: boolean) => void} onBars - noData: nothing older exists
     */
    getBars: (periodParams, onBars) => {
      datafeed.getBars(symbolInfo, resolution, periodParams, (bars, meta) => {
        if (!isClosed) onBars(bars, Boolean(meta?.noData));
      });
    },

    /**
     * @param {(bar: object) => void} onBar - the current bar on every update, a new bar when it starts
     * @param {() => void} onResetNeeded - the stream asks to reload the history
     */
    subscribe: (onBar, onResetNeeded) => {
      subscriberCounter += 1;
      subscriberUid = `${symbol}_${resolution}_${subscriberCounter}`;

      datafeed.subscribeBars(
        symbolInfo,
        resolution,
        (bar) => {
          if (!isClosed) onBar(bar);
        },
        subscriberUid,
        () => {
          if (!isClosed) onResetNeeded();
        },
      );
    },

    /** Unsubscribes from the stream and drops answers still on the way. */
    close: () => {
      isClosed = true;

      if (subscriberUid) datafeed.unsubscribeBars(subscriberUid);
    },
  };
};
